import { Landmark, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ManualPaymentMethod } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStoreDetail } from '@/features/stores';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeletePaymentMethodDialog } from '../components/delete-payment-method-dialog';
import { PaymentMethodDialog } from '../components/payment-method-dialog';
import { PaymentMethodsTable, type PaymentMethodAction } from '../components/payment-methods-table';
import { StripeCard } from '../components/stripe-card';
import { UnpaidOrderHoldCard } from '../components/unpaid-order-hold-card';
import { useUpdatePaymentMethod } from '../hooks/use-payment-method-mutations';
import { usePaymentMethods } from '../hooks/use-payment-methods';

/** The hold is a store setting, so it needs the full store and the settings permissions. */
function UnpaidOrderHoldSection({ storeId }: { storeId: string }) {
  const detail = useStoreDetail(storeId);
  const { editable } = useStoreEditable({ permission: 'settings.write' });
  if (!detail.data) return null;
  return (
    <UnpaidOrderHoldCard key={detail.data.updatedAt} store={detail.data} readOnly={!editable} />
  );
}

/** The store's primary domain, proposed as the first Apple Pay domain; null until the detail loads. */
function usePrimaryDomain(storeId: string, enabled: boolean): string | null {
  const detail = useStoreDetail(storeId, enabled);
  return detail.data?.primaryDomain ?? null;
}

type Editing = { open: false } | { open: true; method: ManualPaymentMethod | null };

function PaymentMethods() {
  const { t } = useTranslation('paymentMethods');
  const store = useStore();
  const query = usePaymentMethods(store.id);
  const update = useUpdatePaymentMethod(store.id);
  const message = useErrorMessage();
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<ManualPaymentMethod | null>(null);
  const methods = query.data ?? [];
  const nextPosition = methods.reduce((max, m) => Math.max(max, m.position + 1), 0);
  const manual = useStoreEditable({ permission: 'payments.manage' });
  const stripe = useStoreEditable({ permission: 'settings.write' });
  const { can } = usePermissions();
  const primaryDomain = usePrimaryDomain(store.id, can('settings.read'));

  const onAction = (action: PaymentMethodAction, method: ManualPaymentMethod) => {
    switch (action) {
      case 'edit':
        setEditing({ open: true, method });
        return;
      case 'toggle':
        update.mutate(
          { id: method.id, body: { isActive: !method.isActive } },
          { onError: (error) => toast.error(message(error)) },
        );
        return;
      case 'delete':
        setDeleting(method);
    }
  };

  const addButton = (tour?: 'settings.payment-methods.add') =>
    manual.editable ? (
      <Button data-tour={tour} onClick={() => setEditing({ open: true, method: null })}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name, currency: store.defaultCurrency })}
        actions={addButton('settings.payment-methods.add')}
      />
      <ReadOnlyBanner reason={manual.reason === 'closed' ? 'closed' : null} />
      <Can permission="settings.read">
        <StripeCard storeId={store.id} editable={stripe.editable} primaryDomain={primaryDomain} />
        <div className="mb-6">
          <UnpaidOrderHoldSection storeId={store.id} />
        </div>
      </Can>
      <h2 id={SECTION_ANCHORS.manualMethods} className="mb-3 text-base font-semibold">
        {t('manualTitle')}
      </h2>
      <Alert className="mb-4">
        <Landmark aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      {query.isPending ? (
        <Skeleton className="h-48" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : methods.length === 0 ? (
        <EmptyState
          icon={Landmark}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="settings.payment-methods.empty"
          tourLink
        />
      ) : (
        <PaymentMethodsTable
          methods={methods}
          editable={manual.editable}
          onAction={onAction}
          busyId={update.isPending ? update.variables.id : null}
        />
      )}
      {editing.open ? (
        <PaymentMethodDialog
          key={editing.method?.id ?? 'new'}
          storeId={store.id}
          currency={store.defaultCurrency}
          method={editing.method}
          nextPosition={nextPosition}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <DeletePaymentMethodDialog
        storeId={store.id}
        method={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function PaymentMethodsPage() {
  return (
    <FeatureErrorBoundary name="paymentMethods">
      <PaymentMethods />
    </FeatureErrorBoundary>
  );
}
